import React, { useState, useEffect } from 'react';
import { CreditCard, Mail, Cpu, HardDrive, Memory, Wifi, Database, Clock,
  Activity, Server, RefreshCw, Zap, Terminal, Shield,
  AlertTriangle, Search, CheckCircle, XCircle, BarChart3,
  TrendingUp, ArrowUp, ArrowDown, FileText, Loader2
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function ServerHealth() {
  const [data, setData] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [deepHealth, setDeepHealth] = useState(null);
  const [cardProvider, setCardProvider] = useState('flutterwave');
  const [changingProvider, setChangingProvider] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealthLight = async () => {
    try {
      const res = await api.get('/api/admin/server-health/light');
      setData(prev => {
        const updated = { ...prev };
        if (!updated.server) updated.server = {};
        updated.server.cpu = { ...updated.server.cpu, percent: res.data.cpu_percent, perCore: res.data.cpu_per_core || [], serverCores: res.data.cpu_server_cores || 3 };
        updated.server.memory = { ...updated.server.memory, percent: res.data.memory_percent, used_gb: res.data.memory_used_gb };
        updated.server.disk = { ...updated.server.disk, percent: res.data.disk_percent };
        if (!updated.health_checks) updated.health_checks = {};
        updated.health_checks.browser_alive = res.data.browser_alive;
        updated.health_checks.queue_depth = res.data.queue_depth || 0;
        updated.health_checks.running_jobs = res.data.running_jobs || 0;
        updated.health_checks.queued_jobs = res.data.queued_jobs || 0;
        return updated;
      });
    } catch {}
  };

  const fetchHealth = async () => {
    try {
      const res = await api.get('/api/admin/server-health');
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchReadiness = async () => {
    try { const res = await api.get('/api/health/ready'); setReadiness(res.data); } catch {}
  };

  const fetchWorkerStatus = async () => {
    try { const res = await api.get('/api/health/workers'); setWorkerStatus(res.data); } catch {}
  };

  const fetchDeepHealth = async () => {
    try { const res = await api.get('/api/health/deep'); setDeepHealth(res.data); } catch {}
  };

  const fetchDiagnostics = async () => {
    try { const res = await api.get('/api/health/diagnostics'); setDiagnostics(res.data); } catch {}
  };

  const fetchCardProvider = async () => {
    try { 
      const res = await api.get('/api/billing/active-card-provider');
      setCardProvider(res.data.provider); 
    } catch {}
  };

  useEffect(() => { fetchHealth(); fetchReadiness(); fetchWorkerStatus(); fetchDiagnostics(); fetchCardProvider(); }, []);
  
  useEffect(() => {
    const interval = setInterval(() => {
      fetchReadiness();
      fetchWorkerStatus();
      fetchDiagnostics();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchHealthLight, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;
  if (!data) return <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>Unable to load server stats</p>;

  const { server, process: proc, database: db, network } = data;
  const cpuColor = server.cpu.percent > 90 ? 'var(--error)' : server.cpu.percent > 70 ? 'var(--warning)' : 'var(--success)';
  const memColor = server.memory.percent > 85 ? 'var(--error)' : server.memory.percent > 70 ? 'var(--warning)' : 'var(--success)';
  const diskColor = server.disk.percent > 85 ? 'var(--error)' : server.disk.percent > 70 ? 'var(--warning)' : 'var(--success)';

  // Determine overall status
  const overallStatus = readiness?.status === 'ready' ? 'operational' : readiness?.status === 'degraded' ? 'degraded' : 'outage';
  const statusColors = {
    operational: { bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', dot: '#10B981', text: 'var(--success)', label: 'Operational' },
    degraded: { bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', dot: '#F59E0B', text: 'var(--warning)', label: 'Degraded' },
    outage: { bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)', dot: '#EF4444', text: 'var(--error)', label: 'Outage' },
  };
  const statusStyle = statusColors[overallStatus];

  return (
    <div className="page-content" style={{ maxWidth: '100%' }}>
      
      {/* ─── TIER 1: Status Banner ─── */}
      <div style={{
        background: statusStyle.bg, border: `1px solid ${statusStyle.border}`,
        borderRadius: 'var(--radius-xl)', padding: '16px 24px', marginBottom: 'var(--space-xl)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: statusStyle.dot, boxShadow: `0 0 8px ${statusStyle.dot}` }} />
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: statusStyle.text, margin: 0 }}>
              System {statusStyle.label}
            </h2>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '2px 0 0' }}>
              Updated {new Date(data.timestamp).toLocaleTimeString()} · Uptime: {server.uptime.days}d {server.uptime.hours}h
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setAutoRefresh(!autoRefresh)} style={{
            padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border-primary)',
            background: autoRefresh ? 'var(--accent-glow)' : 'transparent',
            color: autoRefresh ? 'var(--accent-primary)' : 'var(--text-secondary)',
            cursor: 'pointer', fontSize: 11, fontWeight: 600,
          }}><RefreshCw size={12} /> {autoRefresh ? 'Live' : 'Paused'}</button>
          <button onClick={() => { fetchHealth(); fetchReadiness(); fetchWorkerStatus(); fetchDiagnostics(); }} style={{
            padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border-primary)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600,
          }}><RefreshCw size={12} /> Refresh All</button>
        </div>
      </div>

      {/* ─── TIER 2: Resources ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <ResourceCard icon={<Cpu size={18} />} label="CPU" percent={server.cpu.percent} color={cpuColor} detail={`${server.cpu.cores} cores · ${server.cpu.frequency_mhz}MHz · Load ${(server.load_average?.['1min'] || 0).toFixed(1)}`} perCore={server.cpu.perCore} serverCores={server.cpu.serverCores} />
        <ResourceCard icon={<HardDrive size={18} />} label="Memory" percent={server.memory.percent} color={memColor} detail={`${server.memory.used_gb} / ${server.memory.total_gb} GB · Swap ${server.memory.swap_percent}%`} />
        <ResourceCard icon={<HardDrive size={18} />} label="Disk" percent={server.disk.percent} color={diskColor} detail={`${server.disk.used_gb} / ${server.disk.total_gb} GB`} />
      </div>

      {/* ─── TIER 3: System Health Grid ─── */}
      {readiness && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
          <SectionHeader icon={<Activity size={14} />} title="System Health" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
            <HealthCard icon={<Database size={16} />} label="Database" status={readiness.checks?.database?.status} detail={`${readiness.checks?.database?.latency_ms || 0}ms`} />
            <HealthCard icon={<Server size={16} />} label="DB Pool" status={readiness.checks?.db_pool?.status} detail={`${readiness.checks?.db_pool?.used || 0}/${readiness.checks?.db_pool?.max || 0}`} />
            <HealthCard icon={<Clock size={16} />} label="Job Queue" status={readiness.checks?.jobs?.status} detail={`${readiness.checks?.jobs?.running || 0} run · ${readiness.checks?.jobs?.queued || 0} queued`} />
            <HealthCard icon={<HardDrive size={16} />} label="Disk" status={readiness.checks?.disk?.status} detail={`${readiness.checks?.disk?.free_gb || 0}GB free`} />
            <HealthCard icon={<Cpu size={16} />} label="Workers" status={workerStatus?.status === 'healthy' ? 'up' : workerStatus?.status === 'idle' ? 'warning' : 'down'} detail={workerStatus?.status || 'Unknown'} />
            <HealthCard icon={<Wifi size={16} />} label="Browser" status={data.health_checks?.browser_alive ? 'up' : 'down'} detail={data.health_checks?.browser_alive ? 'Active' : 'Dead'} />
          </div>
        </div>
      )}

      {/* ─── TIER 4: Worker Activity ─── */}
      {workerStatus && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
          <SectionHeader icon={<RefreshCw size={14} />} title="Worker Activity (Last 15 min)" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 16 }}>
            <StatPill label="Running" value={workerStatus.workers?.running_jobs || 0} color="var(--info)" />
            <StatPill label="Queued" value={workerStatus.workers?.queued_jobs || 0} color="var(--warning)" />
            <StatPill label="Completed" value={workerStatus.workers?.completed_15m || 0} color="var(--success)" />
            <StatPill label="Failed (1h)" value={workerStatus.workers?.failed_1h || 0} color={workerStatus.workers?.failed_1h > 0 ? 'var(--error)' : 'var(--success)'} />
            <StatPill label="Stuck" value={workerStatus.workers?.stuck_jobs || 0} color={workerStatus.workers?.stuck_jobs > 0 ? 'var(--error)' : 'var(--success)'} />
          </div>
          {workerStatus.workers?.stuck_details?.length > 0 && (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--error)', marginBottom: 6 }}>⚠️ Stuck Jobs</div>
              {workerStatus.workers.stuck_details.map((job, i) => (
                <div key={i} style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 }}>{job.query},  {job.started_at}</div>
              ))}
            </div>
          )}
          {workerStatus.recent_jobs?.length > 0 && (
            <>
              <h5 style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Recent Jobs</h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>User</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Query</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Leads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workerStatus.recent_jobs.slice(0, 10).map((job, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{job.started_at ? new Date(job.started_at).toLocaleTimeString() : '-'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{job.user_email || '-'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.query}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 600,
                            background: job.status === 'completed' ? 'var(--success-bg)' : job.status === 'failed' ? 'var(--error-bg)' : job.status === 'running' ? 'var(--info-bg)' : 'var(--warning-bg)',
                            color: job.status === 'completed' ? 'var(--success)' : job.status === 'failed' ? 'var(--error)' : job.status === 'running' ? 'var(--info)' : 'var(--warning)',
                          }}>{job.status}</span>
                        </td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{job.result_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── TIER 5: Search Diagnostics ─── */}
      {diagnostics && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', boxShadow: 'var(--shadow-sm)', marginBottom: 16 }}>
          <SectionHeader icon={<Search size={14} />} title="Search Diagnostics (24h)" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
            <StatPill label="Searches" value={diagnostics.search_diagnostics?.total_24h || 0} color="var(--info)" />
            <StatPill label="Success Rate" value={`${diagnostics.search_diagnostics?.success_rate || 100}%`} color="var(--success)" />
            <StatPill label="Avg Duration" value={`${diagnostics.search_diagnostics?.avg_duration_sec || 0}s`} color="var(--accent-primary)" />
            <StatPill label="Avg Leads" value={diagnostics.search_diagnostics?.avg_leads || 0} color="var(--warning)" />
            <StatPill label="Failed" value={diagnostics.search_diagnostics?.failed_24h || 0} color={diagnostics.search_diagnostics?.failed_24h > 0 ? 'var(--error)' : 'var(--success)'} />
          </div>
          {diagnostics.recent_searches?.length > 0 && (
            <>
              <h5 style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Recent Searches</h5>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>User</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Query</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Leads</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-tertiary)', fontWeight: 500 }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnostics.recent_searches.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{s.created_at ? new Date(s.created_at).toLocaleTimeString() : '-'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{s.user_email || '-'}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.search_query}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{
                            padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 600,
                            background: s.status === 'completed' ? 'var(--success-bg)' : 'var(--error-bg)',
                            color: s.status === 'completed' ? 'var(--success)' : 'var(--error)',
                          }}>{s.status}</span>
                        </td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{s.leads_found || 0}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>{s.total_duration_sec ? `${s.total_duration_sec}s` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── TIER 6: Database & Backup ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px' }}>
          <SectionHeader icon={<Database size={14} />} title="Database Stats" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[['Users', db.users_rows], ['Businesses', db.service_businesses_rows], ['Enrichments', db.client_enrichments_rows], ['Audit Logs', db.audit_logs_rows]].map(([name, count]) => (
              <div key={name} style={{ textAlign: 'center', padding: 8, background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{(count || 0).toLocaleString()}</div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{name}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px' }}>
          <SectionHeader icon={<FileText size={14} />} title="Backup Status" />
          {diagnostics?.backup && diagnostics.backup.recent?.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                    <th style={backupThStyle}>Backup Name</th>
                    <th style={backupThStyle}>Time</th>
                    <th style={backupThStyle}>Size</th>
                    <th style={{ ...backupThStyle, textAlign: 'center' }}>Storage</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnostics.backup.recent.map((b, i) => (
                    <tr key={b.name} style={{ borderBottom: i < diagnostics.backup.recent.length - 1 ? '1px solid var(--border-primary)' : 'none', fontWeight: i === 0 ? 700 : 400 }}>
                      <td style={{ ...backupTdStyle, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? 'var(--accent-amber)' : 'var(--text-primary)' }}>{b.name}</td>
                      <td style={backupTdStyle}>{b.modified}</td>
                      <td style={backupTdStyle}>{b.size_kb} KB</td>
                      <td style={{ ...backupTdStyle, textAlign: 'center' }}>
                        <span title="Local backup exists" style={{ marginRight: 6 }}>💾</span>
                        <span title="Google Drive sync available">📧</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─── TIER 7: Controls ─── */}
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', marginBottom: 16 }}>
        <SectionHeader icon={<Terminal size={14} />} title="Maintenance Controls" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-md)' }}>
          <ControlButton label="Clear Stuck Jobs" desc="Remove jobs > 10min" endpoint="/api/admin/server-health/clear-stuck-jobs" onDone={fetchHealth} />
          <ControlButton label="Restart Browser" desc="Restart Playwright pool" endpoint="/api/admin/server-health/restart-browser" onDone={fetchHealth} />
          <ControlButton label="Vacuum Database" desc="Reclaim disk space" endpoint="/api/admin/server-health/vacuum-db" onDone={fetchHealth} />
          <ControlButton label="Deep Health Check" desc="Run all diagnostics" endpoint="/api/health/deep" onDone={fetchDeepHealth} />
          <button onClick={async () => {
            try {
              await api.post('/api/admin/send-daily-digest');
              toast.success('Daily digest sent! Check your inbox.');
            } catch {
              toast.error('Failed to send digest');
            }
          }} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '10px 16px', borderRadius: 10,
            border: '1px dashed var(--accent-primary)', background: 'var(--accent-glow)',
            color: 'var(--accent-primary)', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Mail size={14} /> Send Test Digest
          </button>
        
      {/* Payment Settings */}
      <div style={{ marginTop: 24, padding: 16, background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border-primary)' }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
          <CreditCard size={16} style={{ marginRight: 8 }} /> Card Payment Provider
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Active Provider:</span>
          <select value={cardProvider} onChange={async (e) => {
            const newProvider = e.target.value;
            if (newProvider === cardProvider) return;
            if (!window.confirm(`Switch card payment provider to ${newProvider}? Existing subscriptions remain unaffected.`)) return;
            setChangingProvider(true);
            try {
              await api.post('/api/admin/system-settings', { key: 'card_payment_provider', value: newProvider });
              setCardProvider(newProvider);
              toast.success(`Card payment provider set to ${newProvider}`);
            } catch (err) {
              toast.error('Failed to update provider');
            } finally {
              setChangingProvider(false);
            }
          }} disabled={changingProvider} style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border-primary)',
            background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer'
          }}>
            <option value="flutterwave">Flutterwave</option>
            <option value="paystack">Paystack</option>
          </select>
          {changingProvider && <Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }} />}
        </div>
        <p style={{ color: 'var(--text-tertiary)', fontSize: 11, marginTop: 8 }}>
          Only new card payments will use the selected provider. Existing subscriptions and crypto payments are unaffected.
        </p>
      </div>
</div>
      </div>

      {/* ─── TIER 8: Security ─── */}
      {diagnostics && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-xl)', padding: '20px 24px', marginBottom: 16 }}>
          <SectionHeader icon={<Shield size={14} />} title="Security" />
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <StatPill label="Failed Logins (24h)" value={diagnostics.failed_logins_24h || 0} color={diagnostics.failed_logins_24h > 0 ? 'var(--error)' : 'var(--success)'} />
          </div>
        </div>
      )}

      {/* ─── TIER 9: Server Details ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
        <StatCard icon={<Clock size={18} />} label="Uptime" value={`${server.uptime.days}d ${server.uptime.hours}h`} color="var(--accent-primary)" />
        <StatCard icon={<Terminal size={18} />} label="Processes" value={proc.total_count.toLocaleString()} sub={`Python: ${proc.python_memory_mb}MB · ${proc.python_threads} threads`} color="var(--accent-secondary)" />
        <StatCard icon={<Database size={18} />} label="Database" value={`${db.size_mb} MB`} sub={`${db.table_count || '?'} tables`} color="var(--warning)" />
        <StatCard icon={<Wifi size={18} />} label="Network" value={`${network.bytes_sent_gb}GB ↑`} sub={`${network.bytes_recv_gb}GB ↓`} color="var(--success)" />
        <StatCard icon={<Activity size={18} />} label="Python Process" value={`${proc.python_cpu_percent}% CPU`} sub={`${proc.python_connections} connections`} color="var(--info)" />
      </div>

    </div>
  );
}

// ── Section Header Component ──
function SectionHeader({ icon, title }) {
  return (
    <h4 style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 14px', paddingBottom: 10, borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
      {icon} {title}
    </h4>
  );
}

// ── Resource Card ──
function ResourceCard({ icon, label, percent, color, detail, perCore, serverCores }) {
  const scaleHeight = 60; // px for the vertical scale
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>{icon} {label}</span>
        <span style={{ fontSize: 22, fontWeight: 700, color }}>{percent}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-tertiary)', overflow: 'hidden', marginBottom: perCore && perCore.length > 0 ? 18 : 0 }}>
        <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.5s ease' }} />
      </div>
      {perCore && perCore.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: scaleHeight + 18, marginBottom: 6, paddingLeft: 32, position: 'relative' }}>
            {/* Vertical scale */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 18, width: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 4 }}>
              {[100, 75, 50, 25, 0].map(v => (
                <div key={v} style={{ fontSize: 8, color: 'var(--text-tertiary)', lineHeight: 1 }}>{v}%</div>
              ))}
            </div>
            {/* Horizontal grid lines */}
            <div style={{ position: 'absolute', left: 28, right: 0, top: 0, bottom: 18, pointerEvents: 'none' }}>
              {[100, 75, 50, 25, 0].map(v => (
                <div key={v} style={{ position: 'absolute', bottom: `${v}%`, left: 0, right: 0, borderTop: '1px dashed var(--border-primary)', opacity: v === 0 ? 0 : 0.5 }} />
              ))}
            </div>
            {/* Bars */}
            {perCore.map((pct, i) => {
              const isServer = serverCores !== undefined && i < serverCores;
              const barHeight = Math.max((pct / 100) * scaleHeight, 2);
              const barColor = pct > 80 ? 'var(--error)' : pct > 50 ? 'var(--warning)' : isServer ? 'var(--info)' : 'var(--success)';
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                  <div style={{ width: '100%', height: barHeight, borderRadius: '2px 2px 0 0', background: barColor, transition: 'height 0.3s ease' }} />
                  <div style={{ fontSize: 7, color: 'var(--text-tertiary)', marginTop: 2, position: 'absolute', bottom: 0 }}>{i}</div>
                </div>
              );
            })}
          </div>
          {serverCores !== undefined && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, fontSize: 9, color: 'var(--text-tertiary)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--info)' }} /> Server (cores 0-{serverCores-1})</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--success)' }} /> Workers</span>
            </div>
          )}
        </>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>{detail}</div>
    </div>
  );
}

// ── Health Card (for system health grid) ──
function HealthCard({ icon, label, status, detail }) {
  const statusColor = status === 'up' ? 'var(--success)' : status === 'warning' ? 'var(--warning)' : 'var(--error)';
  return (
    <div style={{ padding: '12px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', textAlign: 'center' }}>
      <div style={{ color: statusColor, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 2 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor }} />
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{detail}</span>
      </div>
    </div>
  );
}


const backupThStyle = { padding: '8px 10px', textAlign: 'left', color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const backupTdStyle = { padding: '8px 10px', fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };

// ── Stat Pill ──
function StatPill({ label, value, color }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 700, color: color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
    </div>
  );
}

// ── Stat Card ──
function StatCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-xl)', boxShadow: 'var(--shadow-sm)', textAlign: 'center' }}>
      <div style={{ color, marginBottom: 8, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Control Button ──
function ControlButton({ label, desc, endpoint, onDone }) {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  
  const handleClick = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await api.post(endpoint);
      setResult(res.data);
      if (onDone) setTimeout(onDone, 1000);
    } catch (e) {
      setResult({ status: 'error', message: 'Request failed' });
    } finally { setLoading(false); }
  };
  
  return (
    <div style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-lg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
        <button onClick={handleClick} disabled={loading} style={{
          padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border-primary)',
          background: loading ? 'var(--bg-tertiary)' : 'transparent',
          color: loading ? 'var(--text-tertiary)' : 'var(--accent-primary)',
          cursor: loading ? 'default' : 'pointer', fontSize: 11, fontWeight: 600,
        }}>{loading ? 'Running...' : 'Run'}</button>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: 0 }}>{desc}</p>
      {result && <p style={{ fontSize: 11, color: result.status === 'ok' ? 'var(--success)' : 'var(--error)', margin: '6px 0 0' }}>{result.message || 'Done'}</p>}
    </div>
  );
}
